import {
  AnnotationIcon,
  BookOpenIcon,
  CalendarIcon,
  ChatAlt2Icon,
  ClipboardCheckIcon,
  ClockIcon,
  CreditCardIcon,
  GlobeAltIcon,
  IdentificationIcon,
  InboxIcon,
  LightningBoltIcon,
  MailIcon,
  PauseIcon,
  ShoppingBagIcon,
  SwitchHorizontalIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import WhatsAppBusinessApp from '../mocks/WhatsAppBusinessApp';
import WhatsAppChat from '../mocks/WhatsAppChat';
import WhatsAppConnect from '../mocks/WhatsAppConnect';
import type { ChannelContent, DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';

const BRAND: DemoBrand = {
  name: 'Honeycrust Bakery',
  initial: 'H',
  bg: 'bg-[#b45309]',
};

/*
 * Facts this page relies on (checked 2026-10-21): WhatsApp is not in
 * FREE_PLAN_CHANNELS; the bots server answers `type: 'text'` messages only
 * and keeps the last 20 messages per customer; actions run as tools there;
 * templates can be created and submitted for approval, but the agent never
 * sends them; coexistence needs the number on the WhatsApp Business app for
 * 7+ days (docs/agents/whatsapp.mdx).
 */
export function useWhatsAppContent(): ChannelContent {
  const { t } = useChannelsT();
  const { t: tc } = useLocale('chatbots');
  const finalReply = t(
    'All set, Marta! Your chocolate fudge cake will be ready on Saturday at 11:00. 🎂',
  );

  return {
    key: 'whatsapp',
    name: 'WhatsApp',
    logo: 'FaWhatsapp',
    seoTitle: t('WhatsApp AI Chatbot for Business: Answer Customers 24/7'),
    seoDescription: t(
      'Put an AI agent on your WhatsApp Business number. It answers customers 24/7 from your own knowledge, takes orders and books appointments. Connect in one click.',
    ),
    hero: {
      label: t('WhatsApp AI chatbot'),
      title: t('Answer every WhatsApp message.'),
      accent: t('Even the ones at 2 a.m.'),
      lead: t(
        'Connect your WhatsApp Business number in one click. Your AI agent answers customers from your website and documents, takes orders and books appointments, day and night.',
      ),
      facts: [
        t('One-click setup with Facebook'),
        t('Keeps your WhatsApp Business app'),
        t('WhatsApp on paid plans'),
      ],
      description: t(
        'Example: a customer asks a bakery on WhatsApp for a chocolate cake on Saturday. The agent quotes the price from the menu, asks for a name and confirms the pickup time, then saves the order and emails it to the bakery.',
      ),
      demo: (
        <WhatsAppChat
          brand={BRAND}
          lines={[
            {
              from: 'customer',
              text: t('Hi! Can I order a chocolate cake for Saturday?'),
              time: '10:41',
              at: 600,
            },
            {
              from: 'agent',
              text: t(
                'Of course! Our chocolate fudge cake serves 10 and costs $42. What name should I put the order under?',
              ),
              time: '10:41',
              typingAt: 1100,
              at: 2000,
            },
            {
              from: 'customer',
              text: t('Marta Silva. I’ll pick it up at 11, please.'),
              time: '10:42',
              at: 3100,
            },
            {
              from: 'agent',
              text: finalReply,
              time: '10:42',
              typingAt: 3600,
              at: 4500,
            },
          ]}
        />
      ),
      steps: [
        {
          icon: BookOpenIcon,
          title: t('Checked the cake menu'),
          via: tc('Knowledge'),
          at: 1500,
        },
        {
          icon: ClipboardCheckIcon,
          title: t('Saved the order'),
          via: tc('Collect data'),
          at: 3900,
        },
        {
          icon: MailIcon,
          title: t('Emailed it to the bakery'),
          via: t('Email notification'),
          at: 4300,
        },
      ],
    },
    jobs: {
      title: t('Every message gets an answer.'),
      tail: t('And the work behind it gets done.'),
      lead: t(
        'Customers expect a quick reply on WhatsApp. Your agent answers straight away from what you’ve taught it, in the customer’s own language, and takes care of the follow-up.',
      ),
      items: [
        {
          icon: ChatAlt2Icon,
          title: t('Answers questions'),
          detail: t(
            'Prices, opening hours, delivery areas and policies, straight from your website and documents.',
          ),
        },
        {
          icon: ShoppingBagIcon,
          title: t('Takes orders'),
          detail: t(
            'Asks for the details it needs in the chat, then emails them to your team or sends them to your system.',
          ),
        },
        {
          icon: CalendarIcon,
          title: t('Books appointments'),
          detail: t(
            'Checks when you’re free and books the time in your Google Calendar.',
          ),
        },
        {
          icon: IdentificationIcon,
          title: t('Captures leads'),
          detail: t(
            'Collects names, emails and anything else you ask for, one question at a time, and saves them to Leads.',
          ),
        },
        {
          icon: GlobeAltIcon,
          title: t('Speaks their language'),
          detail: t(
            'Replies in the language each customer writes in, from the same knowledge.',
          ),
        },
        {
          icon: LightningBoltIcon,
          title: t('Checks your systems'),
          detail: t(
            'Looks up an order or a booking in your own system with a custom action.',
          ),
        },
      ],
    },
    setup: {
      title: t('Live on WhatsApp in minutes.'),
      tail: t('No developer account needed.'),
      steps: [
        {
          title: t('Build your agent'),
          detail: t(
            'Add your website, menu or documents and write instructions in plain words. Try it in the Playground first.',
          ),
        },
        {
          title: t('Connect your number'),
          detail: t(
            'Click Continue with Facebook and pick or create your WhatsApp Business number in Meta’s window. Already on the WhatsApp Business app? Connect that number instead.',
          ),
        },
        {
          title: t('Say hello'),
          detail: t(
            'Send a message from another phone. Your agent replies within seconds, and the chat shows up in Conversations.',
          ),
        },
      ],
      guide: 'https://easy-peasy.ai/docs/agents/whatsapp',
      guideLabel: t('Read the WhatsApp setup guide'),
      description: t(
        'The Connect WhatsApp window, with a Continue with Facebook button and an option for numbers already on the WhatsApp Business app, next to the bakery’s number shown as connected.',
      ),
      visual: <WhatsAppConnect brand={BRAND} />,
    },
    spotlight: {
      title: t('Keep your WhatsApp Business app.'),
      tail: t('Your agent answers, and you still can.'),
      lead: t(
        'Connect the number you already use in the WhatsApp Business app. Your agent answers automatically, you read and reply from your phone as before, and your chat history stays.',
      ),
      points: [
        t('Same number, same chat history'),
        t('Read and reply from the app whenever you like'),
        t('You confirm the link in the app on your phone'),
        t('Meta requires 7 days of use on the app first'),
      ],
      description: t(
        'The bakery owner’s phone with the WhatsApp Business app open, listing chats the agent answered from the same number, and a note that messages work in both places.',
      ),
      visual: (
        <WhatsAppBusinessApp
          chats={[
            {
              name: 'Marta Silva',
              preview: finalReply,
              time: '10:42',
              avatar: 'bg-[linear-gradient(140deg,#f59e0b,#b45309)]',
            },
            {
              name: 'Diego Ramos',
              preview: t('Yes! Our gluten-free loaf is baked every Friday.'),
              time: '10:31',
              avatar: 'bg-[linear-gradient(140deg,#38bdf8,#1d4ed8)]',
            },
            {
              name: 'Priya Nair',
              preview: t('We open at 7:30 on weekdays and 8:00 at weekends.'),
              time: '09:58',
              avatar: 'bg-[linear-gradient(140deg,#f472b6,#be185d)]',
            },
            {
              name: 'Tom Becker',
              preview: t(
                'Delivery to Riverside costs $4 and takes about an hour.',
              ),
              time: '09:12',
              avatar: 'bg-[linear-gradient(140deg,#34d399,#047857)]',
            },
            {
              name: 'Aisha Bello',
              preview: t('You’re welcome! See you on Saturday.'),
              time: t('Yesterday'),
              avatar: 'bg-[linear-gradient(140deg,#a78bfa,#6d28d9)]',
            },
          ]}
        />
      ),
    },
    rules: {
      title: t('Good to know.'),
      tail: t('WhatsApp’s rules, in plain words.'),
      items: [
        {
          icon: CreditCardIcon,
          title: t('Paid plans'),
          detail: t(
            'WhatsApp is on paid plans, and you can build and test your agent for free first. Meta charges its own fees for business messaging, billed to your Meta account.',
          ),
        },
        {
          icon: ClockIcon,
          title: t('The 24-hour window'),
          detail: t(
            'Your agent replies freely within 24 hours of a customer’s last message. To message someone first, WhatsApp requires an approved template.',
          ),
        },
        {
          icon: AnnotationIcon,
          title: t('Text messages'),
          detail: t(
            'Your agent answers text messages. Photos, voice notes and documents aren’t answered on WhatsApp.',
          ),
        },
        {
          icon: SwitchHorizontalIcon,
          title: t('One AI per number'),
          detail: t(
            'WhatsApp lets a number use one messaging service at a time, so it answers through Meta’s own AI agent or through yours, not both.',
          ),
          link: {
            href: '/blog/meta-business-agent-vs-your-own-ai-agent',
            label: t('Meta’s agent or your own?'),
          },
        },
        {
          icon: InboxIcon,
          title: t('Every chat in one inbox'),
          detail: t(
            'WhatsApp chats show up in Conversations, next to your website, Messenger, Telegram and LINE chats.',
          ),
        },
        {
          icon: PauseIcon,
          title: t('Pause at any time'),
          detail: t(
            'Switch the agent off and messages still reach your number. If you disconnect, the number stays in your Meta account.',
          ),
        },
      ],
    },
    faqs: [
      {
        question: t('Do I need the WhatsApp Business API?'),
        answer: t(
          'Not as a separate step. You sign in with Facebook in our window and pick or create your number, and your agent then answers through Meta’s official WhatsApp Business Platform. There’s no developer app, token or webhook to set up.',
        ),
      },
      {
        question: t('Can I keep using the WhatsApp Business app?'),
        answer: t(
          'Yes. Choose “I already use the WhatsApp Business app” when you connect. Your agent answers automatically and you can still read and reply from the app. Meta requires the number to have been in use on the app for at least 7 days, and the option isn’t available in every country.',
        ),
      },
      {
        question: t('Can I use a number that’s on regular WhatsApp?'),
        answer: t(
          'Not while it’s signed in to the regular WhatsApp app. Delete that WhatsApp account on the phone, wait a few minutes and connect again, or connect a number from the WhatsApp Business app instead.',
        ),
      },
      {
        question: t('How much does it cost?'),
        answer: t(
          'WhatsApp is on paid plans, and you can build and test your agent on the Free plan first. Meta charges its own fees for business messaging, billed to your Meta account.',
        ),
      },
      {
        question: t('Can the agent message customers first?'),
        answer: t(
          'It replies to the messages customers send. To start a conversation, or to write more than 24 hours after a customer’s last message, WhatsApp requires an approved message template.',
        ),
      },
      {
        question: t('Does it understand voice notes and photos?'),
        answer: t(
          'On WhatsApp it answers text messages; photos, voice notes and documents aren’t answered. If customers need to send photos, the chat on your website and Telegram can read them once image attachments are turned on.',
        ),
      },
      {
        question: t(
          'Is this the same as Meta’s AI in the WhatsApp Business app?',
        ),
        answer: t(
          'No. Meta’s agent lives inside the WhatsApp Business app. Yours learns from your own website and documents, can take orders and book appointments, and answers on your website and other channels too. A number can run one of them at a time.',
        ),
      },
      {
        question: t('Which languages does it speak?'),
        answer: t(
          'It replies in the language each customer writes in, more than 80 of them, from the same knowledge.',
        ),
      },
    ],
    finalTitle: t('Your agent could be answering WhatsApp by lunchtime.'),
    finalExchange: {
      question: t('Do you have gluten-free bread today?'),
      answer: t('Yes, our buckwheat loaf. Shall I keep one for you?'),
    },
  };
}
